/**
 * @fileoverview Tipos TypeScript para sistema DRE Editável
 * @see RELATÓRIO 4 - DRE EDITÁVEL COM VERSIONAMENTO
 * @created 2025-10-17
 */

/**
 * Representa um ano fiscal na DRE
 * 
 * **Campos Editáveis** (6):
 * - receitaBruta, impostosVendas, custosDirectos
 * - despesasOperacionais, depreciacao, impostosLucro
 * 
 * **Campos Calculados** (9):
 * - receitaLiquida, lucroBruto, margemBruta
 * - ebitda, margemEbitda, ebit, margemEbit
 * - lucroLiquido, margemLiquida
 */
export interface DREYear {
  year: number;
  
  // ========================================
  // CAMPOS EDITÁVEIS (6 campos)
  // ========================================
  
  /** Receita Bruta (R$) - editável */
  receitaBruta: number;
  
  /** (-) Impostos sobre Vendas (R$) - editável */
  impostosVendas: number;
  
  /** (-) Custos Diretos (R$) - editável */
  custosDirectos: number;
  
  /** (-) Despesas Operacionais (R$) - editável */
  despesasOperacionais: number;
  
  /** (-) Depreciação (R$) - editável */
  depreciacao: number;
  
  /** (-) Impostos sobre Lucro (R$) - editável */
  impostosLucro: number;
  
  // ========================================
  // CAMPOS CALCULADOS (9 campos - readonly)
  // ========================================
  
  /** Receita Líquida = Receita Bruta - Impostos Vendas */
  readonly receitaLiquida: number;
  
  /** Lucro Bruto = Receita Líquida - Custos Diretos */
  readonly lucroBruto: number;
  
  /** % Margem Bruta = (Lucro Bruto / Receita Líquida) × 100 */
  readonly margemBruta: number;
  
  /** EBITDA = Lucro Bruto - Despesas Operacionais */
  readonly ebitda: number;
  
  /** % Margem EBITDA = (EBITDA / Receita Líquida) × 100 */
  readonly margemEbitda: number;
  
  /** EBIT = EBITDA - Depreciação */
  readonly ebit: number;
  
  /** % Margem EBIT = (EBIT / Receita Líquida) × 100 */
  readonly margemEbit: number;
  
  /** Lucro Líquido = EBIT - Impostos Lucro */
  readonly lucroLiquido: number;
  
  /** % Margem Líquida = (Lucro Líquido / Receita Líquida) × 100 */
  readonly margemLiquida: number;
}

/**
 * DRE de uma empresa (Sétima ou 555 Studios)
 */
export interface DRECompany {
  companyId: 'setima' | '555';
  companyName: string;
  years: DREYear[];
}

/**
 * Estado completo da DRE (ambas empresas)
 */
export interface DREData {
  id: string;
  userId: string;
  setima: DRECompany;
  studio555: DRECompany;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Tipo de versão da DRE (master = original, editable = modificável)
 */
export type VersionType = 'master' | 'editable';

/**
 * Campos editáveis da DRE (para validação)
 */
export type DREEditableField = 
  | 'receitaBruta'
  | 'impostosVendas'
  | 'custosDirectos'
  | 'despesasOperacionais'
  | 'depreciacao'
  | 'impostosLucro';

/**
 * Campos calculados da DRE (readonly)
 */
export type DRECalculatedField = 
  | 'receitaLiquida'
  | 'lucroBruto'
  | 'margemBruta'
  | 'ebitda'
  | 'margemEbitda'
  | 'ebit'
  | 'margemEbit'
  | 'lucroLiquido'
  | 'margemLiquida';

/**
 * Todos os campos da DRE
 */
export type DREField = DREEditableField | DRECalculatedField;

/**
 * Estado de validação
 */
export interface ValidationState {
  isValid: boolean;
  errors: DREValidationError[];
  warnings: DREValidationError[];
  infos: DREValidationError[];
}

/**
 * Resultado de validação de um conjunto de anos
 */
export interface ValidationResult extends ValidationState {
  company: 'setima' | '555';
  yearCount: number;
}

/**
 * Erro de validação da DRE
 */
export interface DREValidationError {
  company: 'setima' | '555';
  year: number;
  field: DREField;
  message: string;
  severity: 'error' | 'warning';
}

/**
 * Metadata sobre linha DRE (para UI)
 */
export interface DRELineMetadata {
  field: DREField;
  label: string;
  editable: boolean;
  format: 'currency' | 'percent';
  negative: boolean;
  calculated: boolean;
  highlight: boolean;
  indentLevel: 0 | 1 | 2;
  cssClass?: string;
}

/**
 * Definição completa das linhas DRE (ordem de exibição)
 */
export const DRE_LINES: DRELineMetadata[] = [
  {
    field: 'receitaBruta',
    label: 'Receita Bruta',
    editable: true,
    format: 'currency',
    negative: false,
    calculated: false,
    highlight: false,
    indentLevel: 0,
  },
  {
    field: 'impostosVendas',
    label: '(-) Impostos s/ Vendas',
    editable: true,
    format: 'currency',
    negative: true,
    calculated: false,
    highlight: false,
    indentLevel: 1,
  },
  {
    field: 'receitaLiquida',
    label: 'Receita Líquida',
    editable: false,
    format: 'currency',
    negative: false,
    calculated: true,
    highlight: false,
    indentLevel: 0,
    cssClass: 'bg-slate-800/30',
  },
  {
    field: 'custosDirectos',
    label: '(-) Custos Diretos',
    editable: true,
    format: 'currency',
    negative: true,
    calculated: false,
    highlight: false,
    indentLevel: 1,
  },
  {
    field: 'lucroBruto',
    label: 'Lucro Bruto',
    editable: false,
    format: 'currency',
    negative: false,
    calculated: true,
    highlight: false,
    indentLevel: 0,
    cssClass: 'bg-slate-800/30',
  },
  {
    field: 'margemBruta',
    label: '% Margem Bruta',
    editable: false,
    format: 'percent',
    negative: false,
    calculated: true,
    highlight: false,
    indentLevel: 2,
    cssClass: 'italic text-slate-400',
  },
  {
    field: 'despesasOperacionais',
    label: '(-) Despesas Operacionais',
    editable: true, // ✅ EDITÁVEL! (Bidirecional: editar Desp → calcula EBITDA)
    format: 'currency',
    negative: true,
    calculated: false, // ✅ Pode ser INPUT ou OUTPUT (depende do que usuário edita)
    highlight: false,
    indentLevel: 1,
  },
  {
    field: 'ebitda',
    label: 'EBITDA',
    editable: true, // ✅ EDITÁVEL! (Bidirecional: editar EBITDA → calcula Desp)
    format: 'currency',
    negative: false,
    calculated: false, // ✅ Pode ser INPUT ou OUTPUT (depende do que usuário edita)
    highlight: true,
    indentLevel: 0,
    cssClass: 'bg-slate-800/30 font-bold',
  },
  {
    field: 'margemEbitda',
    label: '% Margem EBITDA',
    editable: false,
    format: 'percent',
    negative: false,
    calculated: true,
    highlight: false,
    indentLevel: 2,
    cssClass: 'italic text-slate-400',
  },
  {
    field: 'depreciacao',
    label: '(-) Depreciação',
    editable: true,
    format: 'currency',
    negative: true,
    calculated: false,
    highlight: false,
    indentLevel: 1,
  },
  {
    field: 'ebit',
    label: 'EBIT',
    editable: false,
    format: 'currency',
    negative: false,
    calculated: true,
    highlight: false,
    indentLevel: 0,
    cssClass: 'bg-slate-800/30',
  },
  {
    field: 'margemEbit',
    label: '% Margem EBIT',
    editable: false,
    format: 'percent',
    negative: false,
    calculated: true,
    highlight: false,
    indentLevel: 2,
    cssClass: 'italic text-slate-400',
  },
  {
    field: 'impostosLucro',
    label: '(-) Impostos s/ Lucro',
    editable: true,
    format: 'currency',
    negative: true,
    calculated: false,
    highlight: false,
    indentLevel: 1,
  },
  {
    field: 'lucroLiquido',
    label: 'Lucro Líquido',
    editable: false,
    format: 'currency',
    negative: false,
    calculated: true,
    highlight: true,
    indentLevel: 0,
    cssClass: 'bg-slate-800/30 font-bold',
  },
  {
    field: 'margemLiquida',
    label: '% Margem Líquida',
    editable: false,
    format: 'percent',
    negative: false,
    calculated: true,
    highlight: false,
    indentLevel: 2,
    cssClass: 'italic text-slate-400',
  },
];

/**
 * Cria um ano DRE vazio (para adicionar novo ano)
 * 
 * @param year - Ano fiscal (ex: 2025)
 * @param previousYear - (Opcional) Ano anterior para estimar valores iniciais
 * @param company - (Opcional) Empresa para ajustar % de depreciação (Sétima: 3%, 555: 2%)
 * @returns Ano DRE com todos os campos zerados (ou estimados se previousYear fornecido)
 * 
 * @example
 * // Ano vazio
 * const emptyYear = createEmptyDREYear(2025);
 * 
 * // Ano com estimativas baseado em 2024
 * const year2024 = { year: 2024, receitaBruta: 8000000, depreciacao: 240000, ... };
 * const estimatedYear = createEmptyDREYear(2025, year2024, 'setima');
 * // → depreciacao: 240000 (mantém do ano anterior ou 3% se não houver)
 */
export function createEmptyDREYear(
  year: number, 
  previousYear?: DREYear,
  company: 'setima' | '555' = 'setima'
): DREYear {
  // Calcular depreciação sugerida se ano anterior fornecido
  let depreciacaoSugerida = 0;
  if (previousYear && previousYear.receitaBruta > 0) {
    // Se ano anterior tem depreciação, manter proporção
    if (previousYear.depreciacao > 0) {
      const percentual = previousYear.depreciacao / previousYear.receitaBruta;
      depreciacaoSugerida = previousYear.receitaBruta * percentual;
    } else {
      // Caso contrário, usar % padrão por empresa
      const percentualPadrao = company === 'setima' ? 0.03 : 0.02; // 3% Sétima, 2% 555
      depreciacaoSugerida = previousYear.receitaBruta * percentualPadrao;
    }
  }

  return {
    year,
    // Campos editáveis
    receitaBruta: 0,
    impostosVendas: 0,
    custosDirectos: 0,
    despesasOperacionais: 0,
    depreciacao: Math.round(depreciacaoSugerida), // 🆕 Sugestão automática
    impostosLucro: 0,
    // Campos calculados (zerados)
    receitaLiquida: 0,
    lucroBruto: 0,
    margemBruta: 0,
    ebitda: 0,
    margemEbitda: 0,
    ebit: 0,
    margemEbit: 0,
    lucroLiquido: 0,
    margemLiquida: 0,
  };
}
